import { Box } from '@mui/material';
import { useMemo } from 'react';
import { SectionTitle } from '@shared/components/SectionTitle';

import { ProfileReportContentWrapper } from '@styles/ContactManager/HeaderStyles';
import { formatCurrency } from '@shared/commonFunctions';
import { BorderWrapper } from '@styles/CommonStyles';
import { FieldType, ICreateTradeSummary, IReportSummary } from './interfaces/interfaces';


export const buildReportSummaryFields = (summary: IReportSummary['summary']): ICreateTradeSummary => ({
  'Total# of Trades:': { value: summary?.totalTrades ?? '' },
  'Current Trades:': { value: summary?.currentTrades ?? '' },
  'Unrated Trades:': { value: summary?.unratedTrades ?? '' },
  'Curr Neg Trades:': { value: summary?.currNegTrades ?? '' },
  'Hist Neg Trades:': { value: summary?.histNegTrades ?? '' },
  'No. of Accts Paid:': { value: summary?.numberAcctsPaid ?? '' },
  'Current Past Due:': { value: summary?.currentPastDue ?? '', type: FieldType.currency },
  'Amount Past Due:': { value: summary?.amountPastDue ?? '', type: FieldType.currency },
  'days30:': { value: summary?.days30 ?? '' },
  'days60:': { value: summary?.days60 ?? '' },
  'days90:': { value: summary?.days90 ?? '' },
  'Inquiries:': { value: summary?.inquiriesCount ?? '' },
  'Inq. Last 6 Mnths:': { value: summary?.inquiriesInLast6Month ?? '' },
  'Public Records:': { value: summary?.publicRecords ?? '' },
  'collections:': { value: summary?.collections ?? '' },
  'Oldest Trade Date:': { value: summary?.oldestTradeDate ?? '' },
  'Accounts Balance:': { value: summary?.accountsBalance ?? '', type: FieldType.currency },
  'Monthly Payment:': { value: summary?.monthlyPayment ?? '', type: FieldType.currency },
  'Credit Limit:': { value: summary?.creditLimit ?? '', type: FieldType.currency },
  'High Credit:': { value: summary?.highBalance ?? '', type: FieldType.currency },
  'Total Real Est Balance:': { value: summary?.totalRealEstBalance ?? '', type: FieldType.currency },
  'Total Rev Balance:': { value: summary?.totalRevBalance ?? '', type: FieldType.currency },
  'Total Instalment Balance:': { value: summary?.totInstalmentBalance ?? '', type: FieldType.currency },
  'Available Percent:': { value: summary?.availablePercent ?? '' },
});

export const REPORT_SUMMARY_SECTION_BOUNDS: [number, number][] = [
  [0, 8],
  [8, 16],
  [16, 25],
];

export const ReportSummary = ({ summary }: IReportSummary) => {
  const createTradeSummary: ICreateTradeSummary = useMemo(() => buildReportSummaryFields(summary), [summary]);

  const keys = createTradeSummary && Object.keys(createTradeSummary);
  const buildSummary = (start: number, bound: number) => {
    const fragment = [];
    for (; start < bound; start++) {
      fragment.push(
        <ProfileReportContentWrapper>
          <div className="bold">{keys && keys[start]}</div>
          <div className="summary-value">
            {keys &&
              (createTradeSummary[keys[start]]?.type === FieldType.currency
                ? formatCurrency(createTradeSummary[keys[start]]?.value)
                : createTradeSummary[keys[start]]?.value)}
          </div>
        </ProfileReportContentWrapper>
      );
    }
    return fragment;
  };

  return (
    <Box marginBottom={2} marginTop={3}>
      <SectionTitle title="Report Summary" fontWeight={600} />
      <BorderWrapper padding="10px 20px 0px 8px">
        <ProfileReportContentWrapper>
          <section style={{ width: '25%' }}>{buildSummary(0, 8)}</section>
          <section style={{ width: '25%' }}>{buildSummary(8, 16)}</section>
          <section style={{ width: '33%' }}>{buildSummary(16, 25)}</section>
        </ProfileReportContentWrapper>
      </BorderWrapper>
    </Box>
  );
};